import { useEffect, useMemo, useState } from 'react'
import type { Media, UserRole } from '@nhanho/shared'
import { slugifyVi } from '@nhanho/shared'
import { useAuth } from '../lib/auth'
import { auth as authApi, users as usersApi, explainError, type ProfileBody } from '../lib/api'
import { useToast } from '../lib/toast'
import { useDirtyGuard } from '../lib/dirty'
import { PageHeader } from '../components/ui/PageHeader'
import { Card } from '../components/ui/Card'
import { Field } from '../components/ui/Field'
import { Input, Textarea } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { ConfirmDialog } from '../components/ui/Modal'
import { ImageField } from '../components/media/ImageField'

const BIO_MAX = 500

interface Profile {
  name: string
  slug: string
  slugTouched: boolean
  avatar: Media | null
  jobTitle: string
  bio: string
  facebook: string
  tiktok: string
  shopee: string
}

const EMPTY: Profile = {
  name: '', slug: '', slugTouched: false, avatar: null, jobTitle: '', bio: '',
  facebook: '', tiktok: '', shopee: '',
}

const toBody = (p: Profile): ProfileBody => ({
  name: p.name,
  slug: p.slug || null,
  avatar: p.avatar?.id ?? null,
  jobTitle: p.jobTitle,
  bio: p.bio,
  socials: { facebook: p.facebook, tiktok: p.tiktok, shopee: p.shopee },
})


/**
 * Nhãn vai cho người dùng đọc. `Record` **đầy đủ** (không `Partial`) — thêm vai mới vào
 * `UserRole` mà quên đặt tên ở đây thì TS gãy lúc build, chứ không hiện ra một ô trống.
 */
const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Admin',
  manager: 'Quản lý nội dung',
  editor: 'Biên tập viên',
}

export default function Account() {
  const { user, setUser } = useAuth()
  const toast = useToast()

  // ─── Hồ sơ tác giả ────────────────────────────────────────────────────────
  const [form, setForm] = useState<Profile>(EMPTY)
  const [initial, setInitial] = useState<Profile>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [confirmSlug, setConfirmSlug] = useState(false)

  useEffect(() => {
    if (!user) return
    const p: Profile = {
      name: user.name ?? '',
      slug: user.slug ?? '',
      slugTouched: true,
      avatar: (typeof user.avatar === 'object' ? (user.avatar as Media | null) : null) ?? null,
      jobTitle: user.jobTitle ?? '',
      bio: user.bio ?? '',
      facebook: user.socials?.facebook ?? '',
      tiktok: user.socials?.tiktok ?? '',
      shopee: user.socials?.shopee ?? '',
    }
    setForm(p)
    setInitial(p)
  }, [user])

  const dirty = useMemo(
    () => JSON.stringify(toBody(form)) !== JSON.stringify(toBody(initial)),
    [form, initial],
  )
  useDirtyGuard(dirty)

  const patch = (p: Partial<Profile>) => setForm((f) => ({ ...f, ...p }))
  const setName = (name: string) =>
    patch({ name, slug: form.slugTouched ? form.slug : slugifyVi(name) })

  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      const r = await usersApi.updateMe(toBody(form))
      setUser(r.user)
      toast.success('Đã lưu hồ sơ tác giả')
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
    } finally {
      setSaving(false)
      setConfirmSlug(false)
    }
  }

  const submitProfile = (e: React.FormEvent) => {
    e.preventDefault()
    // Đổi slug khi đã có trang công khai = đổi URL người khác đang link tới. API tự ghi 301,
    // nhưng người đổi nên biết mình đang làm gì — cùng cảnh báo với slug bài viết.
    if (initial.slug && form.slug !== initial.slug) return setConfirmSlug(true)
    void save()
  }

  // ─── Đổi mật khẩu ─────────────────────────────────────────────────────────
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwErrors({})
    if (next !== confirm) return setPwErrors({ confirm: 'Nhập lại chưa khớp' })
    setBusy(true)
    try {
      await authApi.changePassword(cur, next)
      toast.success('Đã đổi mật khẩu. Các phiên đăng nhập khác đã bị đăng xuất.')
      setCur(''); setNext(''); setConfirm('')
    } catch (err) {
      const { message, fieldErrors } = explainError(err)
      setPwErrors(fieldErrors)
      if (!Object.keys(fieldErrors).length) setPwErrors({ currentPassword: message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title="Tài khoản"
        subtitle={`${user?.name} · ${user?.email} · ${user?.role ? ROLE_LABEL[user.role] : ''}`}
      />

      <Card title="Hồ sơ tác giả" className="max-w-2xl">
        <form onSubmit={submitProfile} className="space-y-4">
          <Field label="Tên hiển thị" required error={errors.name}>
            <Input value={form.name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
          </Field>

          <Field
            label="Đường dẫn trang tác giả"
            hint="Đổi sau khi đã có bài đăng thì hệ thống tự tạo chuyển hướng 301 từ đường dẫn cũ."
            error={errors.slug}
          >
            <div className="flex items-center gap-1.5">
              <span className="shrink-0 text-sm text-ink-muted">/tac-gia/</span>
              <Input
                className="font-mono"
                value={form.slug}
                onChange={(e) => patch({ slug: e.target.value, slugTouched: true })}
                maxLength={120}
                placeholder="tu-sinh-tu-ten"
              />
            </div>
          </Field>

          {/* `as="div"`: `ImageField` không phải một input đơn — xem cảnh báo ở `Field`. */}
          <Field label="Ảnh đại diện" as="div" hint="Hiện ở hộp tác giả cuối bài và trên trang tác giả.">
            <ImageField value={form.avatar} onChange={(m) => patch({ avatar: m })} />
          </Field>

          <Field label="Chức danh" hint='Ví dụ: "Thợ handmade", "Người sáng lập". Hiện cạnh tên và đi vào dữ liệu có cấu trúc cho Google.' error={errors.jobTitle}>
            <Input value={form.jobTitle} onChange={(e) => patch({ jobTitle: e.target.value })} maxLength={100} />
          </Field>

          <Field
            label="Giới thiệu"
            hint={`${form.bio.length}/${BIO_MAX} ký tự — dùng cho hộp tác giả, trang tác giả và thẻ mô tả trên Google.`}
            error={errors.bio}
          >
            <Textarea rows={4} maxLength={BIO_MAX} value={form.bio} onChange={(e) => patch({ bio: e.target.value })} />
          </Field>

          <div className="pt-1">
            <p className="mb-2 text-sm font-medium text-ink">Mạng xã hội</p>
            <p className="mb-3 text-xs text-ink-muted">
              Dán <strong>link đầy đủ tới trang cá nhân</strong>, đừng dán tên miền trơn — link
              vẫn chạy nhưng dẫn sai chỗ và không có gì báo.
            </p>
            <div className="space-y-3">
              <Field label="Facebook" error={errors['socials.facebook']}>
                <Input type="url" placeholder="https://facebook.com/ten-cua-ban" value={form.facebook} onChange={(e) => patch({ facebook: e.target.value })} />
              </Field>
              <Field label="TikTok" error={errors['socials.tiktok']}>
                <Input type="url" placeholder="https://tiktok.com/@ten-cua-ban" value={form.tiktok} onChange={(e) => patch({ tiktok: e.target.value })} />
              </Field>
              <Field label="Shopee" error={errors['socials.shopee']}>
                <Input type="url" placeholder="https://shopee.vn/ten-shop" value={form.shopee} onChange={(e) => patch({ shopee: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" loading={saving} disabled={!dirty}>Lưu hồ sơ</Button>
            {dirty && <span className="text-xs text-ink-muted">Có thay đổi chưa lưu</span>}
          </div>
        </form>

        {initial.slug && (
          <p className="mt-4 border-t border-border pt-3 text-xs text-ink-muted">
            Trang công khai của bạn:{' '}
            <code className="font-mono">/tac-gia/{initial.slug}</code>
          </p>
        )}
      </Card>

      <Card title="Đổi mật khẩu" className="mt-5 max-w-lg">
        <form onSubmit={submitPassword} className="space-y-4">
          <Field label="Mật khẩu hiện tại" required error={pwErrors.currentPassword}>
            <Input type="password" autoComplete="current-password" required value={cur} onChange={(e) => setCur(e.target.value)} />
          </Field>
          <Field label="Mật khẩu mới" required hint="Từ 12 ký tự, có cả chữ và số" error={pwErrors.newPassword}>
            <Input type="password" autoComplete="new-password" required minLength={12} value={next} onChange={(e) => setNext(e.target.value)} />
          </Field>
          <Field label="Nhập lại mật khẩu mới" required error={pwErrors.confirm}>
            <Input type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          <Button type="submit" loading={busy}>Đổi mật khẩu</Button>
        </form>
        <p className="mt-4 text-xs text-ink-muted">
          Đổi mật khẩu sẽ đăng xuất mọi thiết bị khác ngay lập tức. Trước khi mở admin ra Internet, mật khẩu seed ban đầu <strong>bắt buộc</strong> phải được đổi ở đây.
        </p>
      </Card>

      <ConfirmDialog
        open={confirmSlug}
        onCancel={() => setConfirmSlug(false)}
        onConfirm={() => void save()}
        title="Đổi đường dẫn trang tác giả?"
        message={
          <>
            <code className="font-mono">/tac-gia/{initial.slug}</code> sẽ đổi thành{' '}
            <code className="font-mono">/tac-gia/{form.slug}</code>.
            <br />
            Đường dẫn cũ được tự động chuyển hướng 301 nên link đã chia sẻ vẫn tới đúng nơi.
          </>
        }
        confirmLabel="Đổi đường dẫn"
        loading={saving}
      />
    </>
  )
}
